import React, { useEffect, useState, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  CircularProgress,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import { makeStyles } from "tss-react/mui";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TicketsReport from "./TicketsReport";
import PlatformLogo from "../../components/PlatformLogo";
import AdsSummary, {
  LOCALE,
  closureOptionsOf,
  formatMoney,
  formatNumber,
} from "../../components/AdsSummary";
import useQueues from "../../hooks/useQueues";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { toast } from "../../services/toast";

const SOURCE_COLOR = {
  WhatsApp: "#25D366",
  Messenger: "#0084FF",
  Instagram: "#C13584",
  "Google Ads": "#4285F4",
};

const PLATFORM_STORAGE_KEY = "adCampaignsPlatform";

const readPlatform = () => {
  try {
    return localStorage.getItem(PLATFORM_STORAGE_KEY) === "google" ? "google" : "meta";
  } catch {
    return "meta";
  }
};

const CURRENCY_STORAGE_KEY = "adCampaignsCurrency";

const readCurrency = () => {
  try {
    return localStorage.getItem(CURRENCY_STORAGE_KEY) === "COP" ? "COP" : "USD";
  } catch {
    return "USD";
  }
};

const useStyles = makeStyles()((theme) => {
  const border = `1px solid ${theme.palette.divider}`;
  const surface = theme.palette.background.paper;

  return {
    page: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      padding: theme.spacing(0, 0.5, 1),
    },
    topBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: theme.spacing(1.5),
    },
    controls: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    toggle: {
      padding: theme.spacing(0.5, 1.5),
      fontWeight: 600,
      fontSize: 12.5,
    },
    period: {
      minWidth: 170,
      fontSize: 13.5,
      "& .MuiSelect-select": {
        paddingTop: 7,
        paddingBottom: 7,
      },
    },
    syncButton: {
      border,
      borderRadius: 8,
      width: 36,
      height: 36,
    },
    platformTabs: {
      minHeight: 40,
      borderBottom: border,
      marginTop: theme.spacing(-0.5),
    },
    platformTab: {
      minHeight: 40,
      minWidth: 0,
      padding: theme.spacing(0, 2),
      gap: theme.spacing(1),
      textTransform: "none",
      fontWeight: 600,
      fontSize: 14,
    },
    tableCard: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 240,
      border,
      borderRadius: 12,
      background: surface,
      overflow: "hidden",
    },
    tableHead: {
      padding: theme.spacing(0, 1.5),
      borderBottom: border,
    },
    viewTabs: {
      minHeight: 44,
    },
    viewTab: {
      minHeight: 44,
      minWidth: 0,
      padding: theme.spacing(0, 1.5),
      textTransform: "none",
      fontWeight: 600,
      fontSize: 13.5,
    },
    tableBody: {
      flex: 1,
      overflow: "auto",
      padding: theme.spacing(0, 1),
      ...theme.scrollbarStyles,
    },
    table: {
      minWidth: 900,
      "& th": {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        background: "transparent",
        whiteSpace: "nowrap",
      },
      "& td": {
        fontSize: 13.5,
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
      },
      "& tr:last-of-type td": {
        borderBottom: "none",
      },
    },
    row: {
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    campaignName: {
      fontWeight: 500,
    },
    campaignCol: {
      minWidth: 280,
    },
    detail: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    postLink: {
      color: theme.palette.primary.main,
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      "&:hover": { textDecoration: "underline" },
    },
    money: {
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    muted: {
      color: theme.palette.text.disabled,
    },
    result: {
      display: "flex",
      flexWrap: "wrap",
      columnGap: theme.spacing(1.5),
      rowGap: 2,
    },
    resultItem: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 12.5,
      whiteSpace: "nowrap",
    },
  };
});

const AdCampaigns = () => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const puedeEditarCola = user?.profile === "admin";
  const [loading, setLoading] = useState(true);
  const [trayendo, setTrayendo] = useState(false);
  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState(readPlatform);
  const [currency, setCurrency] = useState(readCurrency);
  const [vista, setVista] = useState("resumen");
  const [categoriaCierre, setCategoriaCierre] = useState("");
  const [data, setData] = useState({ campanas: [], totales: {} });
  const [queues, setQueues] = useState([]);
  const { findAll: findAllQueues } = useQueues();

  useEffect(() => {
    (async () => {
      try {
        setQueues(await findAllQueues());
      } catch {
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (ventana, plataforma, moneda) => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/ticket/ad-campaigns/report", {
        params: { days: ventana, platform: plataforma, currency: moneda },
      });
      setData(res);
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days, platform, currency);
  }, [days, platform, currency, load]);

  useEffect(() => {
    setCategoriaCierre("");
  }, [platform]);

  const verCierres = (key) => {
    setCategoriaCierre(key);
    setVista("tickets");
  };

  const cambiarMoneda = (e, value) => {
    if (!value) return;
    setCurrency(value);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, value);
    } catch {
    }
  };

  const cambiarPlataforma = (e, value) => {
    if (!value) return;
    setPlatform(value);
    try {
      localStorage.setItem(PLATFORM_STORAGE_KEY, value);
    } catch {
    }
  };

  const fecha = (v) => (v ? new Date(v).toLocaleDateString(LOCALE) : "—");

  const dinero = formatMoney;

  const totales = data.totales || {};

  const cierresTotales = closureOptionsOf(totales);
  const colorCierre = new Map(cierresTotales.map((c) => [c.key, c.color]));
  const nombreCierre = new Map(cierresTotales.map((c) => [c.key, c.name]));

  const cambiarCola = async (campana, queueId) => {
    try {
      const { data: res } = await api.put(
        `/ticket/ad-campaigns/${campana.adReferralSourceId}/queue`,
        { queueId: queueId || null }
      );

      toast.success(
        res?.reasignados
          ? `Departamento actualizado. ${res.reasignados} ticket(s) sin asignar se movieron a ese departamento.`
          : "Departamento actualizado. Aplica a los tickets que lleguen desde ahora."
      );
      load(days, platform, currency);
    } catch (e) {
      toast.error(e);
    }
  };

  const traerDeMeta = async () => {
    setTrayendo(true);
    try {
      const { data: res } = await api.post("/ticket/ad-campaigns/fetch");
      toast.success(
        `${res.anuncios} anuncio(s) en la cuenta. ${res.nuevos} nuevo(s), ${res.actualizados} actualizado(s).`
      );
      load(days, platform, currency);
    } catch (e) {
      toast.error(e);
    } finally {
      setTrayendo(false);
    }
  };

  const sincronizarGoogle = async () => {
    setTrayendo(true);
    try {
      const { data: res } = await api.post("/google-ads/sync");
      toast.success(
        `${res?.campaigns ?? 0} campaña(s) y ${res?.days ?? 0} día(s) de gasto de los últimos 90 días sincronizados desde Google Ads.`
      );
      load(days, platform, currency);
    } catch (e) {
      toast.error(e);
    } finally {
      setTrayendo(false);
    }
  };

  const verTickets = (c) => {
    navigate(`/tickets?campaign=${encodeURIComponent(c.adReferralSourceId)}`);
  };

  return (
    <MainContainer maxWidth={false}>
      <div className={classes.page}>
        <div className={classes.topBar}>
          <Title>Anuncios</Title>
          <div className={classes.controls}>
            <Tooltip title="Convierte los montos con la TRM oficial de cada día (datos.gov.co).">
              <ToggleButtonGroup value={currency} exclusive size="small" onChange={cambiarMoneda}>
                <ToggleButton value="USD" className={classes.toggle}>USD</ToggleButton>
                <ToggleButton value="COP" className={classes.toggle}>COP</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
            <Select
              size="small"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className={classes.period}
            >
              <MenuItem value={7}>Últimos 7 días</MenuItem>
              <MenuItem value={30}>Últimos 30 días</MenuItem>
              <MenuItem value={90}>Últimos 90 días</MenuItem>
              <MenuItem value={0}>Todo el historial</MenuItem>
            </Select>
            {puedeEditarCola && (
              <Tooltip
                title={
                  platform === "google"
                    ? "Sincronizar campañas y gasto de Google Ads (últimos 90 días)"
                    : "Traer anuncios y gasto de Meta (últimos 90 días)"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    className={classes.syncButton}
                    onClick={platform === "google" ? sincronizarGoogle : traerDeMeta}
                    disabled={trayendo}
                  >
                    {trayendo ? <CircularProgress size={18} /> : <CloudSyncOutlinedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <Tabs
          value={platform}
          onChange={cambiarPlataforma}
          className={classes.platformTabs}
        >
          <Tab
            value="meta"
            icon={<PlatformLogo platform="meta" />}
            iconPosition="start"
            label="Meta"
            className={classes.platformTab}
          />
          <Tab
            value="google"
            icon={<PlatformLogo platform="google" />}
            iconPosition="start"
            label="Google Ads"
            className={classes.platformTab}
          />
        </Tabs>

        <AdsSummary
          totales={totales}
          platform={platform}
          activeClosure={vista === "tickets" ? categoriaCierre : null}
          onClosureClick={verCierres}
        />

        <section className={classes.tableCard}>
          <div className={classes.tableHead}>
            <Tabs value={vista} onChange={(e, v) => setVista(v)} className={classes.viewTabs}>
              <Tab value="resumen" label="Campañas" className={classes.viewTab} />
              <Tab value="tickets" label="Tickets" className={classes.viewTab} />
            </Tabs>
          </div>
          <div className={classes.tableBody}>
        {vista === "tickets" ? (
          <TicketsReport
            campaignOptions={data.campanas || []}
            platform={platform}
            days={days}
            closureOptions={cierresTotales}
            category={categoriaCierre}
            onCategoryChange={setCategoriaCierre}
          />
        ) : (
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.campaignCol}>Campaña</TableCell>
              <TableCell>Departamento</TableCell>
              <TableCell align="right">Invertido</TableCell>
              <TableCell align="right">Costo/ticket</TableCell>
              <TableCell align="right">Tickets</TableCell>
              <TableCell>Resultado</TableCell>
              <TableCell align="right">Último ticket</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : data.campanas?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ borderBottom: "none" }}>
                  <Typography variant="body2" color="textSecondary" sx={{ py: 6 }}>
                    Sin gasto ni tickets de {platform === "google" ? "Google Ads" : "Meta"} en este periodo.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.campanas.map((c) => {
                const esPost = c.tipo === "post";
                const detalle = [
                  platform === "meta" && c.sources?.length ? c.sources.join(" + ") : null,
                  platform === "meta" && !esPost && c.campaignName && c.adTitle && c.adTitle !== c.campaignName ? c.adTitle : null,
                  platform === "google" && c.impressions
                    ? `${formatNumber(c.impressions)} impr. · ${formatNumber(c.clicks)} clics`
                    : null,
                ].filter(Boolean);
                const enCurso = (c.abiertos || 0) + (c.pendientes || 0);
                const resultado = [
                  ...(enCurso ? [{ key: "__en_curso", name: "en curso", total: enCurso, color: null }] : []),
                  ...cierresTotales
                    .filter((cat) => c.cierres?.[cat.key])
                    .map((cat) => ({ ...cat, total: c.cierres[cat.key] })),
                  ...Object.keys(c.cierres || {})
                    .filter((key) => !colorCierre.has(key))
                    .map((key) => ({ key, name: nombreCierre.get(key) || key, total: c.cierres[key], color: "#9e9e9e" })),
                ];

                return (
                  <TableRow
                    key={c.adReferralSourceId}
                    className={classes.row}
                    onClick={() => verTickets(c)}
                  >
                    <TableCell>
                      <div className={classes.campaignName}>
                        {esPost ? (
                          c.url ? (
                            <a
                              href={c.url}
                              target="_blank"
                              rel="noreferrer"
                              className={classes.postLink}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Ver publicación
                              <OpenInNewIcon style={{ fontSize: 13 }} />
                            </a>
                          ) : (
                            "Publicación"
                          )
                        ) : (
                          c.campaignName || c.adTitle || c.adReferralSourceId
                        )}
                      </div>
                      {detalle.length > 0 && (
                        <div className={classes.detail}>
                          {platform === "meta" &&
                            (c.sources || []).map((s) => (
                              <span
                                key={s}
                                className={classes.dot}
                                style={{ backgroundColor: SOURCE_COLOR[s] || "#9e9e9e" }}
                              />
                            ))}
                          {detalle.join(" · ")}
                        </div>
                      )}
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        displayEmpty
                        variant="standard"
                        disableUnderline
                        value={c.queueId || ""}
                        disabled={!puedeEditarCola}
                        onChange={(e) => cambiarCola(c, e.target.value)}
                        sx={{ fontSize: 13, minWidth: 130 }}
                        renderValue={(v) => {
                          if (!v) return <span className={classes.muted}>Sin asignar</span>;
                          const q = queues.find((x) => x.id === v);
                          return <span>{q?.name || "Departamento"}</span>;
                        }}
                      >
                        <MenuItem value="">Sin asignar</MenuItem>
                        {queues.map((q) => (
                          <MenuItem key={q.id} value={q.id}>
                            {q.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    <TableCell align="right">
                      {c.spend != null ? (
                        <Tooltip
                          title={
                            c.originalSpend != null
                              ? `Original: ${dinero(c.originalSpend, c.originalCurrency)} (TRM del día)`
                              : ""
                          }
                        >
                          <span className={classes.money}>{dinero(c.spend, c.currency)}</span>
                        </Tooltip>
                      ) : (
                        <span className={classes.muted}>{esPost ? "gratis" : "—"}</span>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {c.costPerTicket != null ? (
                        dinero(c.costPerTicket, c.currency)
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {c.total || <span className={classes.muted}>0</span>}
                      {c.llegadas > c.total && (
                        <Tooltip title="Entradas desde este anuncio. Es mayor que los tickets porque algún contacto volvió a escribir desde él.">
                          <div className={classes.detail}>{c.llegadas} llegadas</div>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>
                      {resultado.length ? (
                        <div className={classes.result}>
                          {resultado.map((r) => (
                            <span key={r.key} className={classes.resultItem}>
                              {r.color && <span className={classes.dot} style={{ backgroundColor: r.color }} />}
                              {r.total} {r.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {c.ultimo ? (
                        fecha(c.ultimo)
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        )}
          </div>
        </section>
      </div>
    </MainContainer>
  );
};

export default AdCampaigns;
